import React, { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Container } from '@mantine/core';
import {
  IconBellRinging,
  IconCheckupList,
  IconFileCertificate,
  IconUsers,
} from '@tabler/icons-react';
import classes from '../../global/style/Classes.module.less';

import Shell from '../../global/component/organism/Shell/Shell';
import { useTranslation } from 'react-i18next';
import EmailWarningSticky from '../../global/component/molecules/EmailWarningSticky/EmailWarningSticky';
import { useAlerts } from '../../global/entities/Alert/queries';
import { useGetPendingPhysicianEICFConsents } from '../../global/entities/EICFConsents/queries/useGetPendingPhysicianEICFConsents.tsx';
import { useGetCanSignEICF } from '../../global/entities/EICFConsents/queries/useGetCanSignEICF.tsx';
import { useFeatureVisibility } from '../../global/provider/featureVisibilityContext';

const HcpDashboardShell: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { data: alerts } = useAlerts();
  const { data: canSignEICF } = useGetCanSignEICF();
  const { data: pendingEicfConsents } = useGetPendingPhysicianEICFConsents(
    canSignEICF === true,
  );
  const { isFeatureVisible, isLoading: isLoadingFeatures } =
    useFeatureVisibility();
  const location = useLocation();
  const navigate = useNavigate();
  const segments = location.pathname.split('/').filter(Boolean);

  const unreadAlertCount = useMemo(() => {
    if (!alerts) return 0;
    return alerts.filter(
      (a) =>
        !a.isVisited &&
        ['Open', 'Acknowledged', 'Escalated'].includes(a.status),
    ).length;
  }, [alerts]);

  const pendingEicfCount = pendingEicfConsents?.length ?? 0;

  const hcpTabs = useMemo(
    () => [
      {
        ids: ['patient', 'invite'],
        icon: IconUsers,
        title: t('page.patient.patient'),
        featureCode: 'patients',
      },
      {
        ids: ['task'],
        icon: IconCheckupList,
        title: t('page.task.task'),
        featureCode: 'tasks',
      },
      {
        ids: ['alert'],
        icon: IconBellRinging,
        title: t('alert.alerts'),
        badge: unreadAlertCount,
        featureCode: 'alerts',
      },
      {
        ids: ['eicfPending', 'eicfConsent'],
        icon: IconFileCertificate,
        title: t('eicf.hcp.listTitle'),
        badge: canSignEICF ? pendingEicfCount : 0,
      },
    ],
    [t, i18n.language, unreadAlertCount, pendingEicfCount, canSignEICF],
  );

  const filteredTabs = useMemo(
    () =>
      hcpTabs.filter(
        (tab) => !tab.featureCode || isFeatureVisible(tab.featureCode),
      ),
    [hcpTabs, isFeatureVisible],
  );

  const firstAvailableRoute = filteredTabs[0]?.ids[0];

  // Check if the current route belongs to a disabled feature
  const isCurrentRouteAccessible = useMemo(() => {
    const currentSegment = segments[1];
    if (!currentSegment) return true;

    const owningTab = hcpTabs.find((tab) => tab.ids.includes(currentSegment));
    if (!owningTab?.featureCode) return true;
    return isFeatureVisible(owningTab.featureCode);
  }, [segments, hcpTabs, isFeatureVisible]);

  // Redirect to the first available tab when on a disabled feature route
  useEffect(() => {
    if (!isLoadingFeatures && !isCurrentRouteAccessible && firstAvailableRoute)
      navigate(firstAvailableRoute, { replace: true });
  }, [
    isCurrentRouteAccessible,
    isLoadingFeatures,
    firstAvailableRoute,
    navigate,
  ]);

  return (
    <>
      <Shell tabs={filteredTabs} route="hcpDashboard">
        <Container size="xl" className={classes.shellContainer}>
          {isCurrentRouteAccessible ? <Outlet /> : null}
        </Container>
      </Shell>
      <EmailWarningSticky />
    </>
  );
};

export default HcpDashboardShell;
